import { Dispatch, SetStateAction } from 'react';
import { useQuery } from 'react-query';
import {
  employerApplicationsKey,
  errorMessage,
  getEmployerApplicationsRoute,
} from '@features/applications';
import axiosInstance from '@server/axiosInstance';
import { Dayjs } from 'dayjs';

type UseGetEmployerApplicationsProps = {
  status: string;
  pageNo: number;
  date?: Dayjs | string | null;
  positions: string[];
  rating: 'All' | number;
  enabled: boolean;
  isWOTCOnly: boolean;
  selectedTag: string
  hired_action?: string
  selectedInterviewType?: string
  setAlertText?: Dispatch<SetStateAction<string>>;
};

export const useGetEmployerApplications = ({
  status,
  date,
  pageNo,
  isWOTCOnly,
  hired_action,
  positions,
  rating,
  enabled,
  selectedTag,
  selectedInterviewType,
  setAlertText = () => { },
}: UseGetEmployerApplicationsProps) => {
  const {
    data: applicationsData,
    isFetching: isLoadingApplications,
    refetch: getApplications,
  } = useQuery({
    queryKey: [employerApplicationsKey, selectedInterviewType, status, hired_action, positions, selectedTag, date, pageNo],
    queryFn: () => {
      const params = new URLSearchParams();

      if (status) {
        params.append('status', status);
      }
      if (hired_action) {
        params.append('hired_action', hired_action === 'All' ? '2' : hired_action);
      }

      if (pageNo) {
        params.append('page', String(pageNo));
      }
      if (selectedInterviewType) {
        params.append('interview_action', String(selectedInterviewType));
      }
      if (selectedTag && selectedTag !== 'All') {
        params.append('tags', selectedTag);
      }

      if (date) {
        if (typeof date === 'string') {
          params.append('date', date);
        } else {
          const formattedDate = date.format('YYYY-MM-DD');
          params.append('date', formattedDate);
        }
      }

      if (isWOTCOnly) {
        params.append('wotc', 'true');
      }

      if (positions && positions.length) {
        params.append('position', positions.join(','));
      }

      if (rating && rating !== 'All') {
        params.append('rating', String(rating));
      }

      return axiosInstance
        .get(getEmployerApplicationsRoute, { params })
        .then((res) => res.data);
    },
    onSuccess: (data) => {
      if (data.object) {
        setAlertText('');
      }
    },
    onError: () => {
      setAlertText(errorMessage);
    },
    enabled,
    refetchOnWindowFocus: false,
  });

  return {
    getApplications,
    applicationsData,
    isLoadingApplications,
  };
};
